import React, { useState, useEffect } from 'react';
import { getColours, getFieldStatus, updateFieldStatus } from '../../lib/treeApi';
import { useTreeAuth } from '../../lib/treeAuth';
import TreeTaskLayout from '../../components/trees/TreeTaskLayout';
import { Plus, Minus, Truck, AlertTriangle, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

import { swatchStyle, registerColours } from '../../lib/treeColors';

export default function TreeFieldStatusTask() {
  const { settings } = useTreeAuth();
  const FULL_LOAD = settings.pallets_per_trailer;
  const [colours, setColours] = useState([]);
  const [fieldStatus, setFieldStatus] = useState({});
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(null);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [colRes, fsRes] = await Promise.all([getColours(), getFieldStatus()]);
      setColours(colRes.data);
      registerColours(colRes.data);
      const statusMap = {};
      (fsRes.data || []).forEach(s => { statusMap[s.color] = s.quantity || 0; });
      setFieldStatus(statusMap);
    } catch { toast.error('Failed to load'); }
    setLoading(false);
  };

  useEffect(() => { fetchData(); }, []);

  const adjustReady = async (color, delta) => {
    const newQty = Math.max(0, (fieldStatus[color] || 0) + delta);
    setUpdating(color);
    try {
      await updateFieldStatus({ color, quantity: newQty });
      setFieldStatus(prev => ({ ...prev, [color]: newQty }));
    } catch { toast.error('Failed to update'); }
    setUpdating(null);
  };

  const totalReady = colours.reduce((s, c) => s + (fieldStatus[c.color] || 0), 0);
  const fullLoads = Math.floor(totalReady / FULL_LOAD);
  const remainder = totalReady % FULL_LOAD;

  return (
    <TreeTaskLayout title="Field Status" subtitle="Mark pallets ready for the lorry">
      <div className="space-y-4" data-testid="tree-field-status-task">
        <div className="flex justify-end">
          <button onClick={fetchData} className="text-xs text-[#4A5D53] flex items-center gap-1" data-testid="tree-task-refresh">
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </button>
        </div>

        {totalReady >= FULL_LOAD && (
          <div className="bg-emerald-50 border-2 border-emerald-400 rounded-2xl p-4 flex items-center gap-3" data-testid="tree-task-full-load-alert">
            <div className="w-11 h-11 rounded-xl bg-emerald-500 flex items-center justify-center flex-shrink-0">
              <Truck className="w-5 h-5 text-white" />
            </div>
            <div>
              <p className="font-bold text-emerald-800 font-['Outfit']">{fullLoads} Full Load{fullLoads > 1 ? 's' : ''} Ready!</p>
              <p className="text-xs text-emerald-600">{totalReady} pallets ready{remainder > 0 ? ` (+${remainder} extra)` : ''}</p>
            </div>
          </div>
        )}
        {totalReady > 0 && totalReady < FULL_LOAD && (
          <div className="bg-amber-50 border border-amber-300 rounded-2xl p-3 flex items-center gap-3" data-testid="tree-task-partial-load-alert">
            <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0" />
            <p className="text-sm text-amber-700"><span className="font-semibold">{totalReady} of {FULL_LOAD}</span> ready &mdash; {FULL_LOAD - totalReady} more for a full load</p>
          </div>
        )}

        <div className="space-y-3" data-testid="tree-task-status-list">
          {colours.map((c) => {
            const ready = fieldStatus[c.color] || 0;
            return (
              <div key={c.color} className="bg-white border border-[#1A4331]/15 rounded-2xl p-4 flex items-center gap-3" data-testid={`tree-task-status-${c.color}`}>
                <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={swatchStyle(c.color)}>
                  <Truck className="w-5 h-5 text-white drop-shadow" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-[#0A1711] font-['Outfit'] capitalize">{c.label || c.color}</p>
                  <p className="text-xs text-[#4A5D53]">ready in field</p>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <button
                    onClick={() => adjustReady(c.color, -1)}
                    disabled={ready === 0 || updating === c.color}
                    className="w-14 h-14 rounded-2xl bg-red-50 border-2 border-red-200 flex items-center justify-center text-red-600 active:scale-95 transition-transform disabled:opacity-30"
                    data-testid={`tree-task-minus-${c.color}`}
                  >
                    <Minus className="w-6 h-6" />
                  </button>
                  <div className="w-12 text-center">
                    <p className="text-3xl font-bold text-[#0A1711] font-['Outfit'] tabular-nums">{ready}</p>
                  </div>
                  <button
                    onClick={() => adjustReady(c.color, 1)}
                    disabled={updating === c.color}
                    className="w-14 h-14 rounded-2xl bg-emerald-50 border-2 border-emerald-200 flex items-center justify-center text-emerald-600 active:scale-95 transition-transform disabled:opacity-30"
                    data-testid={`tree-task-plus-${c.color}`}
                  >
                    <Plus className="w-6 h-6" />
                  </button>
                </div>
              </div>
            );
          })}
          {colours.length === 0 && !loading && (
            <p className="text-center text-sm text-[#4A5D53] py-8">No colours set up yet. Ask an admin to add one.</p>
          )}
        </div>

        {colours.length > 0 && (
          <div className="bg-white border border-[#1A4331]/15 rounded-2xl p-4" data-testid="tree-task-total-ready">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-bold text-[#4A5D53] uppercase tracking-wider">Total Ready</span>
              <span className="text-lg font-bold text-[#0A1711] font-['Outfit']">{totalReady} <span className="text-xs font-normal text-[#4A5D53]">/ {FULL_LOAD}</span></span>
            </div>
            <div className="w-full h-3 bg-[#F4F1ED] rounded-full overflow-hidden">
              <div className={`h-full rounded-full transition-all duration-500 ${totalReady >= FULL_LOAD ? 'bg-emerald-500' : totalReady >= FULL_LOAD * 0.7 ? 'bg-amber-400' : 'bg-[#1A4331]'}`} style={{ width: `${Math.min(100, (totalReady / FULL_LOAD) * 100)}%` }} />
            </div>
          </div>
        )}
      </div>
    </TreeTaskLayout>
  );
}
